/**
 * Usage:
 *
 * <DateRangePicker
 *   value={{ from: dayjs().subtract(30, "day"), to: dayjs() }}
 *   maxRangeDays={30}
 *   minDate={dayjs().subtract(180, "day").startOf("day")}
 *   defaultValue={{ from: dayjs().subtract(30, "day"), to: dayjs() }}
 *   onChange={({ from, to }) => fetchData({ from: from.unix(), to: to.unix() })}
 * />
 *
 * Props:
 *   maxRangeDays – max selectable span between from/to (required from caller).
 *   minDate      – earliest browsable date (independent of maxRangeDays).
 *   defaultValue – range to restore on Clear; if omitted Clear sends null.
 *
 * The first and second selected dates may be in either order. `onChange` is
 * called only after both dates are selected, with normalized `from` and `to`.
 */
import React, { useState, useEffect } from "react";

import dayjs from "dayjs";
import CalendarMonthOutlinedIcon from "@mui/icons-material/CalendarMonthOutlined";
import FirstPageIcon from "@mui/icons-material/FirstPage";
import LastPageIcon from "@mui/icons-material/LastPage";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import { Box, IconButton, Popover, Typography } from "@mui/material";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { DateCalendar } from "@mui/x-date-pickers/DateCalendar";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { PickersDay } from "@mui/x-date-pickers/PickersDay";

import { OutlineButton } from "components/buttons/OutlineButton";
import { SolidButton } from "components/buttons/SolidButton";

const CustomCalendarHeader = (props) => {
  const { currentMonth, onMonthChange, disabled } = props;

  const handlePrevYear = () => {
    const next = currentMonth.subtract(1, "year");
    onMonthChange(next, "left");
  };

  const handleNextYear = () => {
    const next = currentMonth.add(1, "year");
    onMonthChange(next, "right");
  };

  const handlePrevMonth = () => {
    const next = currentMonth.subtract(1, "month");
    onMonthChange(next, "left");
  };

  const handleNextMonth = () => {
    const next = currentMonth.add(1, "month");
    onMonthChange(next, "right");
  };

  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        px: 1,
        py: 0.5,
      }}
    >
      <Box sx={{ display: "flex", alignItems: "center" }}>
        <IconButton
          size="small"
          onClick={handlePrevYear}
          disabled={disabled}
          aria-label="Previous year"
          sx={{ p: 0.5, color: "text.primary" }}
        >
          <FirstPageIcon fontSize="small" />
        </IconButton>
        <IconButton
          size="small"
          onClick={handlePrevMonth}
          disabled={disabled}
          aria-label="Previous month"
          sx={{ p: 0.5, color: "text.primary" }}
        >
          <ChevronLeftIcon fontSize="small" />
        </IconButton>
      </Box>

      <Typography sx={{ fontWeight: 700, fontSize: "14px", color: "text.primary" }}>
        {currentMonth.format("MMMM YYYY")}
      </Typography>

      <Box sx={{ display: "flex", alignItems: "center" }}>
        <IconButton
          size="small"
          onClick={handleNextMonth}
          disabled={disabled}
          aria-label="Next month"
          sx={{ p: 0.5, color: "text.primary" }}
        >
          <ChevronRightIcon fontSize="small" />
        </IconButton>
        <IconButton
          size="small"
          onClick={handleNextYear}
          disabled={disabled}
          aria-label="Next year"
          sx={{ p: 0.5, color: "text.primary" }}
        >
          <LastPageIcon fontSize="small" />
        </IconButton>
      </Box>
    </Box>
  );
};

const DateRangePicker = ({
  value,
  onChange,
  maxRangeDays,
  minDate: propMinDate,
  maxDate,
  defaultValue,
  disableFuture = true,
  ariaLabel = "Select date range",
}) => {
  const [anchorEl, setAnchorEl] = useState(null);
  const [anchorDate, setAnchorDate] = useState(null);
  const [tempRange, setTempRange] = useState(null);

  const minDate = propMinDate ?? undefined;
  const open = Boolean(anchorEl);

  useEffect(() => {
    if (open) {
      if (value?.from && value?.to) {
        const from = dayjs(value.from);
        const to = dayjs(value.to);
        const isSingle = from.isSame(to, "day");
        setTempRange({ from, to, isSingle });
      } else {
        setTempRange(null);
      }
      setAnchorDate(null);
    }
  }, [open, value]);

  const resetSelection = () => setAnchorDate(null);

  const handleClose = () => {
    setAnchorEl(null);
    resetSelection();
  };

  const shouldDisableDate = (date) => {
    if (disableFuture && date.isAfter(dayjs(), "day")) return true;
    if (minDate && date.isBefore(minDate, "day")) return true;
    if (!anchorDate || !maxRangeDays) return false;

    return Math.abs(date.diff(anchorDate, "day")) > maxRangeDays;
  };

  const handleDateChange = (date) => {
    if (!date) return;

    const selectedDate = date.startOf("day");

    if (!anchorDate) {
      setAnchorDate(selectedDate);
      setTempRange({ from: selectedDate, to: selectedDate, isSingle: true });
      return;
    }

    const rangeStart = selectedDate.isBefore(anchorDate, "day") ? selectedDate : anchorDate;
    const rangeEnd = selectedDate.isAfter(anchorDate, "day") ? selectedDate : anchorDate;
    const isSingle = selectedDate.isSame(anchorDate, "day");

    setTempRange({
      from: rangeStart,
      to: rangeEnd,
      isSingle,
    });
    resetSelection();
  };

  const handleApply = () => {
    if (tempRange?.from && tempRange?.to) {
      onChange({
        from: tempRange.from,
        to: tempRange.to,
      });
    } else {
      onChange(null);
    }
    handleClose();
  };

  const handleClear = () => {
    setTempRange(null);
    onChange(defaultValue ?? null);
    handleClose();
  };

  const fromDate = value?.from ? dayjs(value.from) : null;
  const toDate = value?.to ? dayjs(value.to) : null;

  const rangeLabel = fromDate && toDate
    ? (fromDate.isSame(toDate, "day")
        ? fromDate.format("YYYY/MM/DD")
        : `${fromDate.format("YYYY/MM/DD")} - ${toDate.format("YYYY/MM/DD")}`)
    : ariaLabel;

  const rangeStart = tempRange?.from ?? null;
  const rangeEnd = tempRange?.to ?? null;
  const isSingle = Boolean(tempRange?.isSingle || (rangeStart && rangeEnd && rangeStart.isSame(rangeEnd, "day")));

  const RangeDay = (props) => {
    const { day, outsideCurrentMonth, ...dayProps } = props;
    const isStart = rangeStart && day.isSame(rangeStart, "day");
    const isEnd = rangeEnd && day.isSame(rangeEnd, "day");
    const isInRange = !isSingle && rangeStart && rangeEnd
      && day.isAfter(rangeStart, "day")
      && day.isBefore(rangeEnd, "day");
    const isRangeBoundary = !isSingle && Boolean(rangeStart && rangeEnd && (isStart || isEnd));

    return (
      <Box
        sx={{
          backgroundColor: isInRange || isRangeBoundary ? "primary.lightBlue" : "transparent",
          borderRadius: 0,
          ...(isStart && !isSingle && { borderTopLeftRadius: "50%", borderBottomLeftRadius: "50%" }),
          ...(isEnd && !isSingle && { borderTopRightRadius: "50%", borderBottomRightRadius: "50%" }),
          ...(isSingle && isStart && { borderRadius: "50%" }),
        }}
      >
        <PickersDay
          {...dayProps}
          day={day}
          outsideCurrentMonth={outsideCurrentMonth}
          selected={Boolean(isStart || isEnd)}
          sx={{
            ...(isStart || isEnd ? { backgroundColor: "primary.main !important", color: "#ffffff !important" } : {}),
          }}
        />
      </Box>
    );
  };

  const viewReferenceDate = tempRange?.to ?? toDate ?? undefined;

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <IconButton
        aria-label={ariaLabel}
        title={rangeLabel}
        onClick={(event) => setAnchorEl(event.currentTarget)}
        sx={{ color: "primary.main", mx: 0.5 }}
      >
        <CalendarMonthOutlinedIcon />
      </IconButton>
      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
        transformOrigin={{ vertical: "top", horizontal: "center" }}
        slotProps={{
          paper: {
            sx: (theme) => ({
              mt: theme.spacing(2),
              borderRadius: theme.shape.customBorderRadius[3],
              overflow: "hidden",
              p: 2,
              display: "flex",
              flexDirection: "column",
              boxShadow: "0 4px 20px rgba(0,0,0,0.12)",
            }),
          },
        }}
      >
        <Box sx={{ width: "100%" }}>
          <DateCalendar
            sx={{
              height: "auto",
              maxHeight: "none",
              "& .MuiDayCalendar-slideTransition": {
                minHeight: "205px",
              },
            }}
            value={anchorDate ? null : (tempRange?.to ?? null)}
            referenceDate={anchorDate ?? viewReferenceDate}
            showDaysOutsideCurrentMonth
            shouldDisableDate={shouldDisableDate}
            onChange={handleDateChange}
            slots={{
              calendarHeader: CustomCalendarHeader,
              day: RangeDay,
            }}
          />
        </Box>
        <Box display="flex" justifyContent="center" gap={2} mt={1} mb={0.5}>
          <OutlineButton width="96px" height="32px" onClick={handleClear}>
            Clear
          </OutlineButton>
          <SolidButton width="96px" height="32px" onClick={handleApply}>
            Apply
          </SolidButton>
        </Box>
      </Popover>
    </LocalizationProvider>
  );
};

export default DateRangePicker;
